import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Radio,
    InputNumber
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { authService, customerService } from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const { Title } = Typography;

const CustomerCreate = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);

    // Состояние для отслеживания предыдущего тела запроса
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        setPageTitle('Создание клиента');

        setRightContent(
            authService.hasPermission('pos:customers:create') && (
                <Button
                    type="primary"
                    onClick={handleSave}
                    loading={saving}
                >
                    Создать
                </Button>
            )
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Обработка сохранения данных клиента
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                // Преобразуем employee_day_balance в число если поле заполнено
                employee_day_balance: values.employee_day_balance ? parseFloat(values.employee_day_balance) : null
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await customerService.createCustomer(
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Клиент успешно создан');
                // Переходим на страницу созданного клиента
                navigate(`/customers/${response.data.customer.id}`);
            } else {
                message.error(response.message || 'Ошибка при создании клиента');
            }
        } catch (error) {
            console.error('Ошибка при создании:', error);
            message.error(error.message || 'Ошибка при создании клиента');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Title level={3} style={{ marginTop: 0 }}>
                Создание нового клиента
            </Title>

            <Form
                form={form}
                layout="horizontal"
                initialValues={{
                    is_employee: 0
                }}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Личные данные" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Имя"
                                name="first_name"
                                rules={[
                                    { required: true, message: 'Введите имя' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите имя" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Фамилия"
                                name="last_name"
                                rules={[
                                    { required: true, message: 'Введите фамилию' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите фамилию" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Телефон"
                                name="phone"
                                rules={[
                                    { max: 20, message: 'Максимальная длина - 20 символов' }
                                ]}
                            >
                                <Input placeholder="Введите телефон" maxLength={20} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Email"
                                name="email"
                                rules={[
                                    { type: 'email', message: 'Введите корректный email' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите email" maxLength={255} />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Системная информация" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Сотрудник"
                                name="is_employee"
                                rules={[
                                    { required: true, message: 'Выберите статус сотрудника' }
                                ]}
                            >
                                <Radio.Group>
                                    <Radio.Button value={1}>Да</Radio.Button>
                                    <Radio.Button value={0}>Нет</Radio.Button>
                                </Radio.Group>
                            </HorizontalFormItem>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) =>
                                    prevValues.is_employee !== currentValues.is_employee
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('is_employee') === 1 ? (
                                        <HorizontalFormItem
                                            label="Баланс сотрудника на день"
                                            name="employee_day_balance"
                                            rules={[
                                                { required: true, message: 'Введите баланс' },
                                                { type: 'number', min: 0, message: 'Баланс не может быть отрицательным' }
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                placeholder="Введите баланс"
                                                step={1.00}
                                                precision={2}
                                            />
                                        </HorizontalFormItem>
                                    ) : null
                                }
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CustomerCreate;